<template>
  <div class="field">
    <div class="control">
      <label class="label">{{ label }}</label>
      <div class="file has-name is-fullwidth">
        <label class="file-label">
          <input
            ref="input"
            class="file-input"
            type="file"
            accept="image/jpeg,image/png,application/pdf"
            multiple
            @change="onFiles"
          >
          <span class="file-cta">
            <span class="file-icon"><i class="fal fa-paperclip"></i></span>
            <span class="file-label">ເລືອກໄຟລ໌</span>
          </span>
          <span class="file-name">{{ fileNames || 'ຍັງບໍ່ໄດ້ເລືອກ' }}</span>
        </label>
      </div>

      <!--
        Thumbnails, not just names. The mistake this catches is picking the
        wrong screenshot out of a camera roll — a filename cannot show that, and
        the file is unopenable again until after it has been uploaded.
      -->
      <div class="claim-previews" v-if="previews.length">
        <div class="claim-preview" v-for="(p, i) in previews" :key="p.url">
          <img v-if="p.isImage" :src="p.url" :alt="p.name">
          <!-- A PDF has no thumbnail to draw; its name is all there is. -->
          <span v-else class="claim-preview__doc">
            <i class="fal fa-file-pdf"></i>
          </span>
          <span class="claim-preview__name">{{ p.name }}</span>
          <button type="button" class="claim-preview__remove" @click="removeFile(i)" title="ລຶບ">×</button>
        </div>
      </div>

      <p class="claim-hint" v-if="hint">{{ hint }}</p>
      <p class="text-danger" v-if="fileError">{{ fileError }}</p>
    </div>
  </div>
</template>

<script>
/**
 * Pick files for a claim, and SEE what was picked.
 *
 * Extracted on its second use (the contact dialog, then the assessment). Not
 * for tidiness: the two pieces of behaviour worth having once are the object-URL
 * lifecycle — `URL.createObjectURL` holds the whole file in memory until it is
 * revoked — and refusing an oversized file here rather than sending it, since
 * nginx cuts in below PHP's own limit and answers with a bare HTML 413 that
 * nothing in this app can turn into a message. A second hand-rolled copy would
 * have got one of the two wrong.
 *
 * Bulma controls, per the repo's HARD RULE 1.
 */
export default {
  name: 'ClaimFilePicker',

  props: {
    label: { type: String, required: true },
    hint: { type: String, default: '' },

    /** The server's ceiling, from `/config` — NOT this app's own guess. */
    maxFileBytes: { type: Number, default: 1048576 },
  },

  data: () => ({
    files: [],
    fileError: '',
    /** `{url, name, isImage}` — `url` is an object URL that MUST be revoked. */
    previews: [],
  }),

  computed: {
    fileNames() {
      return this.files.map((f) => f.name).join(', ');
    },
  },

  beforeDestroy() {
    this.releasePreviews();
  },

  methods: {
    /**
     * Picking ADDS to the selection; it does not replace it.
     *
     * The browser's own behaviour is to replace, and that reads as "it only takes one photograph":
     * an officer with four pictures of a crushed box opens the dialog four times and ends up with
     * the fourth. Selecting several at once still works — this only means the second visit to the
     * dialog adds to the first.
     *
     * The input itself is cleared after every pick, so choosing the SAME file twice still fires
     * `change` (a browser does not re-fire it for an unchanged value) and the duplicate check
     * below is what refuses it.
     */
    onFiles(event) {
      const picked = Array.from(event.target.files || []);
      const tooBig = picked.find((f) => f.size > this.maxFileBytes);

      // Clear the control straight away: it has given us its files, and leaving the value set
      // makes re-picking the same name a no-op.
      if (this.$refs.input) this.$refs.input.value = '';

      if (tooBig) {
        const mb = (this.maxFileBytes / 1048576).toFixed(1);
        // The files already chosen are KEPT. One oversized pick is not a reason to throw away
        // three good photographs the officer already selected.
        this.fileError = `ໄຟລ໌ "${tooBig.name}" ໃຫຍ່ເກີນ ${mb} MB — ກະລຸນາຫຍໍ້ຮູບກ່ອນ`;
        return;
      }

      // Same name AND same size is the same photograph picked twice — the common mistake when
      // the dialog is opened again, and silently uploading it twice puts two of it on the ERP
      // document an approver reads.
      const fresh = picked.filter(
        (f) => !this.files.some((e) => e.name === f.name && e.size === f.size),
      );

      this.fileError = '';
      this.files = [...this.files, ...fresh];
      this.buildPreviews(this.files);
      this.$emit('input', this.files);
    },

    /** Remove one — the other half of "add": a wrong photograph must be undoable. */
    removeFile(index) {
      this.files.splice(index, 1);
      this.fileError = '';
      this.buildPreviews(this.files);
      this.$emit('input', this.files);
    },

    /** Back to empty — used by the oversize refusal and by the caller on reopen. */
    reset() {
      this.files = [];
      this.fileError = '';
      this.releasePreviews();
      if (this.$refs.input) this.$refs.input.value = '';
    },

    buildPreviews(files) {
      this.releasePreviews();
      this.previews = files.map((file) => ({
        url: URL.createObjectURL(file),
        name: file.name,
        isImage: file.type.startsWith('image/'),
      }));
    },

    /**
     * Revoked before every rebuild, on reset and on destroy. Without this an
     * officer re-picking a few screenshots leaks every one of them for as long
     * as the page stays open.
     */
    releasePreviews() {
      this.previews.forEach((p) => URL.revokeObjectURL(p.url));
      this.previews = [];
    },
  },
};
</script>

<style lang="scss" scoped>
.claim-hint {
  font-size: 12px;
  color: #7a7a7a;
  margin-top: 4px;
}

.claim-previews {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.claim-preview {
  width: 84px;
  position: relative;

  /* Top-right of the thumbnail, where a reader looks to dismiss something. */
  &__remove {
    position: absolute;
    top: -6px;
    right: -6px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    border: 1px solid #dbdbdb;
    background: #fff;
    color: #cc0f35;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    padding: 0;
  }

  img,
  &__doc {
    width: 84px;
    height: 84px;
    border-radius: 4px;
    border: 1px solid #dbdbdb;
    object-fit: cover;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fafafa;
    font-size: 26px;
    color: #7a7a7a;
  }

  &__name {
    display: block;
    font-size: 11px;
    color: #7a7a7a;
    margin-top: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
